import React, { useEffect, useState } from 'react';
import { Plus, Search, Building2, Users, Edit2, Trash2 } from 'lucide-react';
import api from '../lib/api';
import Modal from '../components/Modal';
import { Input, Checkbox } from '../components/Form';

interface Department {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt?: string;
  employeesCount?: number;
}

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const listRes = await api.get('/departments');
      const baseDepartments: Department[] = listRes.data.data || [];

      const withCounts = await Promise.all(
        baseDepartments.map(async (department) => {
          try {
            const detailsRes = await api.get(`/departments/${department.id}`);
            return {
              ...department,
              employeesCount: detailsRes.data?._count?.employees || 0,
            };
          } catch {
            return { ...department, employeesCount: 0 };
          }
        }),
      );

      setDepartments(withCounts);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', isActive: true });
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (department: Department) => {
    setEditing(department);
    setFormData({
      name: department.name || '',
      description: department.description || '',
      isActive: department.isActive ?? true,
    });
    setShowModal(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        isActive: formData.isActive,
      };

      if (editing) {
        await api.patch(`/departments/${editing.id}`, payload);
      } else {
        await api.post('/departments', payload);
      }

      setShowModal(false);
      resetForm();
      await loadData();
    } catch (error: any) {
      console.error('Erro ao salvar departamento:', error);
      alert(error?.response?.data?.message || 'Erro ao salvar departamento.');
    }
  };

  const handleDelete = async (department: Department) => {
    if (!confirm(`Deseja excluir o departamento "${department.name}"?`)) return;
    try {
      await api.delete(`/departments/${department.id}`);
      await loadData();
    } catch (error: any) {
      console.error('Erro ao excluir departamento:', error);
      alert(error?.response?.data?.message || 'Erro ao excluir departamento.');
    }
  };

  const filtered = departments.filter((department) => {
    const term = search.toLowerCase();
    return (
      department.name?.toLowerCase().includes(term) ||
      (department.description || '').toLowerCase().includes(term)
    );
  });

  const totalEmployees = departments.reduce(
    (acc, department) => acc + Number(department.employeesCount || 0),
    0,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando departamentos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Departamentos</h1>
          <p className="text-gray-600 mt-1">Dados oficiais da base de dados</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={openCreate}>
          <Plus className="w-5 h-5" />
          Novo Departamento
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600">Total de Departamentos</p>
          <p className="text-2xl font-bold mt-1">{departments.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Departamentos Ativos</p>
          <p className="text-2xl font-bold mt-1">
            {departments.filter((department) => department.isActive).length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Funcionários Vinculados</p>
          <p className="text-2xl font-bold mt-1">{totalEmployees}</p>
        </div>
      </div>

      <div className="card space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            className="input pl-10"
            placeholder="Buscar departamento..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="space-y-3">
          {filtered.length === 0 ? (
            <p className="text-gray-500">Nenhum departamento encontrado.</p>
          ) : (
            filtered.map((department) => (
              <div
                key={department.id}
                className="border rounded-lg p-4 flex items-start justify-between gap-4"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    <h3 className="font-semibold">{department.name}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        department.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {department.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {department.description || 'Sem descrição'}
                  </p>
                  <p className="text-sm text-gray-700 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {department.employeesCount || 0} funcionário(s)
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100"
                    onClick={() => openEdit(department)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    className="px-3 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100"
                    onClick={() => handleDelete(department)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editing ? 'Editar Departamento' : 'Novo Departamento'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome"
            required
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
          />
          <Input
            label="Descrição"
            value={formData.description}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, description: event.target.value }))
            }
          />
          <Checkbox
            label="Departamento ativo"
            checked={formData.isActive}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, isActive: event.target.checked }))
            }
          />

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Salvar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

